import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';
import { operationsLogger } from '@/lib/logging';
import { ensureValidSession } from '@/lib/auth-utils';

export const ALLOWED_ENTITY_TYPES_FRONTEND = [
  'PROJECT',
  'ASSIGNMENT',
  'SUBPROJECT',
  'TRUCK',
  'ITEM',
  'DAILY_REPORT',
  'COMMENT',
  'PROFILE',
  'COMPANY',
] as const;

export type ParentEntityType = typeof ALLOWED_ENTITY_TYPES_FRONTEND[number];

export interface GetAttachmentsForEntityInput {
  parentEntityId: string; // UUID
  parentEntityType: ParentEntityType;
}

export interface Attachment {
  id: string; // UUID
  parent_entity_id: string; // UUID
  parent_entity_type: ParentEntityType;
  root_assignment_id?: string | null; // UUID, or project_id depending on final schema
  created_by_user_id: string; // UUID - Assuming this was the correct field name from schema
  file_name: string;
  mime_type: string;
  size_bytes: number;
  storage_object_path: string;
  // storage_bucket_id: string; // Removed as it does not exist in the DB table
  created_at: string; // ISO timestamp
  updated_at: string; // ISO timestamp
  // Optional: Add a presigned_url for direct download if generated by the function or fetched separately
  // presigned_url?: string;
}

export async function getAttachmentsForEntity(
  params: GetAttachmentsForEntityInput
): Promise<Attachment[]> {
  if (!params.parentEntityId || !params.parentEntityType) {
    throw new Error('Parent entity ID and type are required.');
  }

  if (!ALLOWED_ENTITY_TYPES_FRONTEND.includes(params.parentEntityType)) {
    throw new Error(`Invalid parent entity type: ${params.parentEntityType}`);
  }

  try {
    const { data, error } = await invokeEdgeFunctionSafely<Attachment[]>('get-attachments-for-entity', {
      body: {
        parentEntityId: params.parentEntityId,
        parentEntityType: params.parentEntityType,
      },
    });

    if (error) {
      operationsLogger.error('attachment-operation', error);
      throw new Error(`Failed to fetch attachments: ${(error as Error).message}`);
    }

    return data || [];
  } catch (e: unknown) {
    operationsLogger.error('getAttachmentsForEntity', e);
    throw e instanceof Error ? e : new Error((e as Error).message || 'An unexpected error occurred while fetching attachments.');
  }
}

// --- New Operation for Download URL ---
export interface GetPresignedDownloadUrlInput {
  attachmentId: string; // UUID
}

export interface GetPresignedDownloadUrlResult {
  presignedUrl: string;
  originalFilename?: string;
  downloadFilename?: string;
}

export async function getPresignedDownloadUrl(
  params: GetPresignedDownloadUrlInput
): Promise<GetPresignedDownloadUrlResult> {
  if (!params.attachmentId) {
    throw new Error('Attachment ID is required.');
  }

  try {
    const { data, error } = await invokeEdgeFunctionSafely<GetPresignedDownloadUrlResult>('get-download-url-for-attachment', {
      body: {
        attachmentId: params.attachmentId,
      },
    });

    if (error) {
      operationsLogger.error('attachment-operation', error);
      throw new Error(`Failed to get download URL: ${(error as Error).message}`);
    }

    if (!data || !data.presignedUrl) {
      throw new Error('Presigned URL was not returned from the function.');
    }

    return data;
  } catch (e: unknown) {
    operationsLogger.error('getPresignedDownloadUrl', e);
    throw e instanceof Error ? e : new Error((e as Error).message || 'An unexpected error occurred while fetching download URL.');
  }
}

// --- Operation for Requesting Temporary Upload URL ---
export interface RequestTemporaryUploadUrlInput {
  fileName: string;
  mimeType: string;
  fileSize: number;
}

export interface RequestTemporaryUploadUrlResult {
  presignedUrl: string;
  tempStoragePath: string; // This is the object path/key in storage
}

export async function requestTemporaryUploadUrl(
  params: RequestTemporaryUploadUrlInput
): Promise<RequestTemporaryUploadUrlResult> {
  if (!params.fileName || !params.mimeType || params.fileSize === undefined) {
    throw new Error('fileName, mimeType, and fileSize are required to request an upload URL.');
  }

  try {
    // CRITICAL: Ensure valid session before upload operations
    // console.warn('[operations] Validating session before requesting upload URL...');
    const sessionValid = await ensureValidSession();
    if (!sessionValid) {
      throw new Error('Authentication session is invalid. Please refresh the page and try again.');
    }
    // console.warn('[operations] Session validated successfully, proceeding with upload URL request');

    const { data, error } = await invokeEdgeFunctionSafely<RequestTemporaryUploadUrlResult>('request-temporary-upload-url', {
      body: {
        fileName: params.fileName,
        mimeType: params.mimeType,
        fileSize: params.fileSize,
      },
    });

    if (error) {
      operationsLogger.error('attachment-operation', error);

      // Handle specific JWT/auth errors with better messaging
      if ((error as Error).message?.includes('missing sub claim') || (error as { code?: string }).code === 'JWT_SUB_CLAIM_MISSING') {
        throw new Error('Authentication token is invalid. Please refresh the page and try again.');
      }

      throw new Error(`Failed to request upload URL: ${(error as Error).message}`);
    }
    
    if (!data || !data.presignedUrl || !data.tempStoragePath) {
      throw new Error('Presigned URL or storage path was not returned from the function.');
    }
    
    // console.warn('[operations] Upload URL requested successfully');
    return data;
  } catch (e: unknown) {
    operationsLogger.error('getPresignedUploadUrl', e);
    throw e instanceof Error ? e : new Error((e as Error).message || 'An unexpected error occurred while requesting upload URL.');
  }
}

// --- Operation for Creating Attachment Metadata in DB ---
export interface CreateAttachmentMetadataInput {
  parent_entity_id: string;
  parent_entity_type: ParentEntityType;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  storage_object_path: string;
  root_assignment_id?: string | null;
}

export async function createAttachmentMetadataEntry(
  params: CreateAttachmentMetadataInput
): Promise<Attachment> { // Assuming the EF returns the full new Attachment object
  try {
    // CRITICAL: Ensure valid session before creating attachment metadata
    // console.warn('[operations] Validating session before creating attachment metadata...');
    const sessionValid = await ensureValidSession();
    if (!sessionValid) {
      throw new Error('Authentication session is invalid. Please refresh the page and try again.');
    }
    // console.warn('[operations] Session validated successfully, proceeding with attachment metadata creation');

    const { data, error } = await invokeEdgeFunctionSafely<Attachment>(
      'create-attachment-metadata',
      {
        body: params, // Pass the whole params object as it matches the EF payload
      }
    );

    if (error) {
      operationsLogger.error('attachment-operation', error);
      
      // Handle specific JWT/auth errors with better messaging
      if ((error as Error).message?.includes('missing sub claim') || (error as { code?: string }).code === 'JWT_SUB_CLAIM_MISSING') {
        throw new Error('Authentication token is invalid. Please refresh the page and try again.');
      }

      throw new Error(`Failed to create attachment record: ${(error as Error).message} (Context: ${(error as { context?: { message?: string } }).context?.message})`);
    }
    if (!data) {
      throw new Error('No data returned from create-attachment-metadata function.');
    }
    
    // console.warn('[operations] Attachment metadata created successfully');
    return data;
  } catch (e: unknown) {
    operationsLogger.error('createAttachmentMetadata', e);
    throw e instanceof Error ? e : new Error((e as Error).message || 'An unexpected error occurred while creating attachment metadata.');
  }
}

// --- Operation for Deleting an Attachment ---
// ASSUMPTION: delete-attachment EF expects { attachmentId: string } and returns a success indicator or the deleted ID.
export interface DeleteAttachmentInput {
  attachmentId: string;
}

export interface DeleteAttachmentResult {
  message?: string; // Or structure it based on actual EF response
  deletedId?: string;
}

export async function deleteAttachmentClientOp(
  params: DeleteAttachmentInput
): Promise<DeleteAttachmentResult> {
  if (!params.attachmentId) {
    throw new Error('attachmentId is required for deleting an attachment.');
  }

  const { data, error } = await invokeEdgeFunctionSafely<DeleteAttachmentResult>(
    'delete-attachment',
    {
      body: { attachmentId: params.attachmentId },
    }
  );

  if (error) {
    operationsLogger.error('attachment-operation', error);
    // More specific error handling could be added if EF returns structured errors
    throw new Error(`Failed to delete attachment ${params.attachmentId}: ${(error as Error).message} (Context: ${(error as { context?: { message?: string } }).context?.message})`);
  }
  
  // If the EF returns no body on success (e.g. 204 No Content), data might be null or undefined.
  // Handle this gracefully.
  return data || { message: `Attachment ${params.attachmentId} processed for deletion.` }; 
}

// Example Usage (conceptual - to be placed in a component):
// import { useQuery } from '@tanstack/react-query';
// import { getAttachmentsForEntity, ParentEntityType } from '@/features/attachments/operations';
// import { getPresignedDownloadUrl } from '@/features/attachments/operations';
//
// const { data: attachments, isLoading, error } = useQuery<Attachment[], Error>(
//   ['attachments', entityId, entityType],
//   () => getAttachmentsForEntity({ parentEntityId: entityId, parentEntityType: entityType as ParentEntityType }),
//   {
//     enabled: !!entityId && !!entityType,
//   }
// );
//
// const handleDownload = async (attachmentId: string) => {
//   try {
//     const { presignedUrl } = await getPresignedDownloadUrl({ attachmentId });
//     window.open(presignedUrl, '_blank'); // Or trigger download differently
//   } catch (_error) {
//     toast({ title: 'Download Failed', description: error.message, variant: 'destructive' });
//   }
// }; 